/**
 * ClienteSeletor — dropdown topbar pra trocar entre clientes (B1.6
 * multi-cliente).
 *
 * Lógica de exibição:
 *   - 0 clientes: não renderiza nada (login estranho — mas defensivo)
 *   - 1 cliente: mostra só o nome (sem dropdown)
 *   - 2+ clientes: dropdown com lista + indicador atual
 *
 * Trocar cliente dispara invalidate cache via ClienteContext.
 *
 * NÃO REVERTER:
 *   - Sem dropdown pra single-cliente (UX limpa pra Portaria fixa)
 *   - Click fora fecha (UX padrão dropdown)
 *   - Cliente atual destacado com checkmark
 *   - Role exibido como badge ao lado do nome (Gestor vs Portaria)
 */

import { useEffect, useRef, useState } from 'react';

import { useCliente } from '@/contexts/ClienteContext';

export function ClienteSeletor() {
  const { clienteAtivo, clientesDisponiveis, temMultiCliente, trocarCliente } = useCliente();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click fora fecha dropdown
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (!clienteAtivo) return null;

  // Single-cliente: só mostra o nome, sem dropdown
  if (!temMultiCliente) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 text-sm"
        data-testid="cliente-seletor-single"
      >
        <ClienteAvatar nome={clienteAtivo.nome} />
        <div className="flex flex-col leading-tight">
          <span className="font-medium">{clienteAtivo.nome}</span>
          <RoleBadge role={clienteAtivo.role} />
        </div>
      </div>
    );
  }

  // Multi-cliente: dropdown
  return (
    <div className="relative" ref={containerRef} data-testid="cliente-seletor-multi">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition text-sm"
        data-testid="cliente-seletor-toggle"
      >
        <ClienteAvatar nome={clienteAtivo.nome} />
        <div className="flex flex-col leading-tight items-start">
          <span className="font-medium max-w-[180px] truncate">{clienteAtivo.nome}</span>
          <RoleBadge role={clienteAtivo.role} />
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`transition flex-shrink-0 ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 mt-1 w-72 bg-paper-surface dark:bg-paper-dark-elevated border border-line dark:border-line-dark rounded shadow-lg py-1 z-20 max-h-[60vh] overflow-y-auto"
          data-testid="cliente-seletor-dropdown"
        >
          <div className="px-3 py-2 text-xs font-medium text-ink-3 dark:text-ink-dark-3 uppercase tracking-wider border-b border-line dark:border-line-dark">
            Trocar de cliente
          </div>
          {clientesDisponiveis.map((c) => {
            const ativo = c.id === clienteAtivo.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  trocarCliente(c.id);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition flex items-center justify-between gap-2 ${
                  ativo ? 'bg-paper-warm dark:bg-paper-dark-warm' : ''
                }`}
                data-testid={`cliente-seletor-item-${c.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ClienteAvatar nome={c.nome} size="sm" />
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="text-sm font-medium truncate">{c.nome}</span>
                    <RoleBadge role={c.role} />
                  </div>
                </div>
                {ativo && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="text-red dark:text-red-glow flex-shrink-0"
                  >
                    <path
                      d="M2.5 7L6 10.5L11.5 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ClienteAvatar({ nome, size = 'md' }: { nome: string; size?: 'sm' | 'md' }) {
  const inicial = nome.charAt(0).toUpperCase();
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm';
  return (
    <div
      className={`${sizeClass} rounded bg-red text-white flex items-center justify-center font-medium flex-shrink-0`}
      aria-hidden="true"
    >
      {inicial}
    </div>
  );
}

function RoleBadge({ role }: { role: 'gestor' | 'portaria' }) {
  return (
    <span className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
      {role === 'gestor' ? 'Gestor' : 'Portaria'}
    </span>
  );
}
